import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n";
import { useEntitlements } from "@/lib/useEntitlements";
import api from "@/lib/api";
import ProtectedLayout from "@/components/Layout";
import PageFallback from "@/components/PageFallback";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import Login from "@/pages/Login";
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const Register = lazy(() => import("@/pages/Register"));
const PdfViewer = lazy(() => import("@/pages/PdfViewer"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Horses = lazy(() => import("@/pages/Horses"));
const Services = lazy(() => import("@/pages/Services"));
const ServiceProviders = lazy(() => import("@/pages/ServiceProviders"));
const Shows = lazy(() => import("@/pages/Shows"));
const AqhaReports = lazy(() => import("@/pages/AqhaReports"));
const AqhaReportReview = lazy(() => import("@/pages/AqhaReportReview"));
const HomeBarn = lazy(() => import("@/pages/HomeBarn"));
const ShowDetail = lazy(() => import("@/pages/ShowDetail"));
const ShowInvoice = lazy(() => import("@/pages/ShowInvoice"));
const Settings = lazy(() => import("@/pages/Settings"));
const ImportWizard = lazy(() => import("@/pages/ImportWizard"));
const Books = lazy(() => import("@/pages/Books"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const Expenses = lazy(() => import("@/pages/Expenses"));
const VetRecords = lazy(() => import("@/pages/VetRecords"));
const Breeding = lazy(() => import("@/pages/Breeding"));
const BreedingPreview = lazy(() => import("@/pages/BreedingPreview"));
const FeaturePreview = lazy(() => import("@/pages/FeaturePreview"));
const InvoicesPreview = lazy(() => import("@/pages/InvoicesPreview"));
const BooksPreview = lazy(() => import("@/pages/BooksPreview"));
const HomeBarnPreview = lazy(() => import("@/pages/HomeBarnPreview"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const PublicInvoice = lazy(() => import("@/pages/PublicInvoice"));
const WallBoard = lazy(() => import("@/pages/WallBoard"));
const PublicBraidList = lazy(() => import("@/pages/PublicBraidList"));
const Landing = lazy(() => import("@/pages/Landing"));
const ShowDay = lazy(() => import("@/pages/ShowDay"));
const BackOffice = lazy(() => import("@/pages/BackOffice"));
const BackOfficeCollegiate = lazy(() => import("@/pages/BackOfficeCollegiate"));
const PhotoDrop = lazy(() => import("@/pages/PhotoDrop"));
const OwnerPortal = lazy(() => import("@/pages/OwnerPortal"));
const PublicResults = lazy(() => import("@/pages/PublicResults"));
const Join = lazy(() => import("@/pages/Join"));
const DisciplineLanding = lazy(() => import("@/pages/DisciplineLanding"));

function NotFoundRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  return <Navigate to={user ? "/dashboard" : "/"} replace />;
}
const SetupWizard = lazy(() => import("@/pages/SetupWizard"));
const Help = lazy(() => import("@/pages/Help"));
const Guide = lazy(() => import("@/pages/Guide"));
const Watch = lazy(() => import("@/pages/Watch"));
const Digest = lazy(() => import("@/pages/Digest"));
const WatchLibrary = lazy(() => import("@/pages/WatchLibrary"));
const PricingPage = lazy(() => import("@/pages/Pricing"));
const Messages = lazy(() => import("@/pages/Messages"));
const CollegiatePortal = lazy(() => import("@/pages/CollegiatePortal"));
const CollegiateLayout = lazy(() => import("@/components/CollegiateLayout"));
const CollegiateDashboard = lazy(() => import("@/pages/collegiate/CollegiateDashboard"));
const CollegiateTeam = lazy(() => import("@/pages/collegiate/CollegiateTeam"));
const CollegiateHorses = lazy(() => import("@/pages/collegiate/CollegiateHorses"));
const CollegiateRideBoards = lazy(() => import("@/pages/collegiate/CollegiateRideBoards"));
const CollegiateMyRides = lazy(() => import("@/pages/collegiate/CollegiateMyRides"));
const CollegiateQuestions = lazy(() => import("@/pages/collegiate/CollegiateQuestions"));
const CollegiateWhiteboard = lazy(() => import("@/pages/collegiate/CollegiateWhiteboard"));
const CollegiateLog = lazy(() => import("@/pages/collegiate/CollegiateLog"));
const CollegiateDayReview = lazy(() => import("@/pages/collegiate/CollegiateDayReview"));
const CollegiateMessages = lazy(() => import("@/pages/collegiate/CollegiateMessages"));
const CollegiateSettings = lazy(() => import("@/pages/collegiate/CollegiateSettings"));
const CollegiateMeets = lazy(() => import("@/pages/collegiate/CollegiateMeets"));
const CollegiateMeetDetail = lazy(() => import("@/pages/collegiate/CollegiateMeetDetail"));
const MeetPublisher = lazy(() => import("@/pages/collegiate/MeetPublisher"));
const MeetWatch = lazy(() => import("@/pages/MeetWatch"));
const ClipPage = lazy(() => import("@/pages/ClipPage"));
const GuestCamera = lazy(() => import("@/pages/GuestCamera"));

// Paid-ad attribution: `?src=fb-whiteboard` on any landing URL is kept until the barn registers.
try {
  const src = new URLSearchParams(window.location.search).get("src");
  if (src && /^[a-z0-9-]{1,40}$/i.test(src)) {
    localStorage.setItem("kingly_src", src.toLowerCase());
    // count the visit (flyer QR scans, ad clicks) so Back Office can show scans → sign-ups
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/src-hit`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ src: src.toLowerCase() }), keepalive: true }).catch(() => {});
  }
} catch { /* storage unavailable */ }

/** Language follows the signed-in user's profile; the toggle persists back to it. */
function LanguageRoot({ children }) {
  const { user } = useAuth();
  const { ent } = useEntitlements(!!user && !user.workspace_type?.startsWith("collegiate"));
  const persist = (l) => { if (user) api.post("/auth/language", { language: l }).catch(() => {}); };
  const userLang = ent && !(ent.features || []).includes("spanish_assistant") ? "en" : user?.language;
  return <LanguageProvider userLang={userLang} onPersist={persist} mtEnabled={!!user}>{children}</LanguageProvider>;
}

function App() {
  return (
    <AuthProvider>
      <LanguageRoot>
      <BrowserRouter>
        <Toaster
          position="top-right"
          richColors
          mobileOffset={{ top: "calc(env(safe-area-inset-top) + 88px)" }}
        />
        <AppErrorBoundary>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/join/:code" element={<Join />} />
          <Route path="/aqha" element={<DisciplineLanding />} />
          <Route path="/nsba" element={<DisciplineLanding />} />
          <Route path="/reining" element={<DisciplineLanding />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/watch" element={<WatchLibrary />} />
          <Route path="/watch/:slug" element={<Watch />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/i/:token" element={<PublicInvoice />} />
          <Route path="/board" element={<WallBoard />} />
          <Route path="/pdf" element={<PdfViewer />} />
          <Route path="/b/:token" element={<PublicBraidList />} />
          <Route path="/admin" element={<BackOffice />} />
          <Route path="/admin/collegiate" element={<BackOfficeCollegiate />} />
          <Route path="/drop/:token" element={<PhotoDrop />} />
          <Route path="/p/:token" element={<OwnerPortal />} />
          <Route path="/r/:token" element={<PublicResults />} />
          <Route path="/setup" element={<SetupWizard />} />
          <Route path="/collegiate" element={<CollegiatePortal />} />
          <Route path="/collegiate/meets/:id/live" element={<MeetPublisher />} />
          <Route path="/m/:token" element={<MeetWatch />} />
          <Route path="/c/:token" element={<ClipPage />} />
          <Route path="/cam/:ptoken" element={<GuestCamera />} />
          <Route element={<CollegiateLayout />}>
            <Route path="/collegiate/app" element={<CollegiateDashboard />} />
            <Route path="/collegiate/meets" element={<CollegiateMeets />} />
            <Route path="/collegiate/meets/:id" element={<CollegiateMeetDetail />} />
            <Route path="/collegiate/horses" element={<CollegiateHorses />} />
            <Route path="/collegiate/ride-boards" element={<CollegiateRideBoards />} />
            <Route path="/collegiate/my-rides" element={<CollegiateMyRides />} />
            <Route path="/collegiate/whiteboard" element={<CollegiateWhiteboard />} />
            <Route path="/collegiate/questions" element={<CollegiateQuestions />} />
            <Route path="/collegiate/log" element={<CollegiateLog />} />
            <Route path="/collegiate/day-review" element={<CollegiateDayReview />} />
            <Route path="/collegiate/messages" element={<CollegiateMessages />} />
            <Route path="/collegiate/settings" element={<CollegiateSettings />} />
            <Route path="/collegiate/team" element={<CollegiateTeam />} />
          </Route>
          <Route element={<ProtectedLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/today" element={<Navigate to="/dashboard" replace />} />
            <Route path="/vet" element={<Navigate to="/vet-records" replace />} />
            <Route path="/money" element={<Navigate to="/invoices" replace />} />
            <Route path="/team" element={<Navigate to="/settings?tab=team" replace />} />
            <Route path="/show-day" element={<ShowDay />} />
            <Route path="/shows" element={<Shows />} />
            <Route path="/aqha/reports" element={<AqhaReports />} />
            <Route path="/aqha/reports/:id" element={<AqhaReportReview />} />
            <Route path="/home-barn" element={<HomeBarn />} />
            <Route path="/whiteboard" element={<Navigate to="/home-barn" replace />} />
            <Route path="/shows/:id" element={<ShowDetail />} />
            <Route path="/shows/:id/invoice/:horseId" element={<ShowInvoice />} />
            <Route path="/horses" element={<Horses />} />
            <Route path="/services" element={<Services />} />
            <Route path="/providers" element={<ServiceProviders />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/import" element={<ImportWizard />} />
            <Route path="/help" element={<Help />} />
            <Route path="/digest" element={<Digest />} />
            <Route path="/books" element={<Books />} />
            <Route path="/accounting" element={<Books />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/recurring" element={<Navigate to="/invoices?tab=recurring" replace />} />
            <Route path="/vet-records" element={<VetRecords />} />
            <Route path="/breeding" element={<Breeding />} />
            <Route path="/breeding/preview" element={<BreedingPreview />} />
            <Route path="/preview/:feature" element={<FeaturePreview />} />
            <Route path="/invoices/preview" element={<InvoicesPreview />} />
            <Route path="/books/preview" element={<BooksPreview />} />
            <Route path="/home-barn/preview" element={<HomeBarnPreview />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:id" element={<Messages />} />
          </Route>
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
        </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
      </LanguageRoot>
    </AuthProvider>
  );
}

export default App;
